import { notification } from "antd"

import { notificationOptions } from "../constants/notification.config"

/** Save a blob under a name, as the browser's own download would. */
export const saveBlob = (blob: Blob, filename: string) => {
  const href = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(href)
}

/** The file behind a URL; an error response is refused rather than saved as the file. */
export const fetchFileBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url, { cache: "no-store" })
  if (!response.ok)
    throw new Error(`Download failed with status ${response.status}`)
  return response.blob()
}

export const downloadFile = async (url: string, name?: string) => {
  try {
    const response = await fetch(url, { cache: "no-store" })
    const blob = await response.blob()

    const fileName = name || "document"
    saveBlob(blob, fileName)

    notification.success({
      message: `${fileName} downloaded successfully.`,
      ...notificationOptions.success
    })
  } catch (error) {
    console.error(`Error downloading file: ${error}`)

    notification.error({
      message: `Failed to download ${name}`,
      ...notificationOptions.error
    })
  }
}

const decodeExtended = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const filenameFromContentDisposition = (
  headers?: Record<string, string>
): string | undefined => {
  const header = Object.entries(headers ?? {}).find(
    ([key]) => key.toLowerCase() === "content-disposition"
  )?.[1]
  if (!header) return undefined

  const extended = /filename\*=\s*[^']*''([^;]+)/i.exec(header)
  const plain = /filename=\s*"([^"]+)"|filename=\s*([^;]+)/i.exec(header)
  const raw = extended
    ? decodeExtended(extended[1])
    : plain && (plain[1] ?? plain[2])

  const filename = raw?.trim().split(/[\\/]/).pop()
  return filename || undefined
}

export const downloadBlob = (blob: Blob, filename: string) => {
  try {
    saveBlob(blob, filename)

    notification.success({
      message: `${filename} downloaded successfully.`,
      ...notificationOptions.success
    })
  } catch (error) {
    console.error(`Error downloading blob: ${error}`)

    notification.error({
      message: `Failed to download ${filename}`,
      ...notificationOptions.error
    })
  }
}
